import { Route, Routes, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useContext, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import './App.css';

import LoginPage from './Pages/LoginPage';
import AdminPage from './Pages/AdminPage';
import StudentPage from './Pages/StudentPage';
import TeacherPage from './Pages/TeacherPage';
import PrivateRoute from './Routes/PrivateRoute';
import AboutUs from "./components/AboutUs";
import IdCard from "./components/IdCard";
import StudentIdCard from "./components/StudentIdCard";
import IdCardAd from './student/components/IdCardAd/IdCardAd';
import GetIdCardPrompt from './student/components/QuickPeek/GetIdCardPrompt';

import { AppContext } from './Context/AppContext';
import { clearLocalStorage, getFromLocalStorage } from './admin/utility/localStorage';

import { doc, getDoc } from 'firebase/firestore';
import { db } from './firebaseConfig';
import AdminRegister from './admin/admin-register';
import StudentSignUp from './Pages/StudentSignUp'; // ✅ NEW IMPORT

function App() {
  const { login } = useContext(AppContext);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (location.pathname === '/register' || location.pathname === '/student-signup') return; // ✅ Skip auto-redirect for signup

    const rememberMe =
        getFromLocalStorage('REMEMBER_ME') === true || getFromLocalStorage('REMEMBER_ME') === 'true';
    const isLoggedIn =
        getFromLocalStorage('IS_LOGGED_IN') === true || getFromLocalStorage('IS_LOGGED_IN') === 'true';
    const userType = getFromLocalStorage('USER_TYPE');
    const primaryKey = getFromLocalStorage('PRIMARY_KEY');

    const fetchUserInfo = async () => {
      try {
        const collectionMap = {
          _ADMIN_: 'admins',
          _STUDENT_: 'students',
          _TEACHER_: 'teachers',
        };
        const roleMap = {
          _ADMIN_: 'admin',
          _STUDENT_: 'student',
          _TEACHER_: 'teacher',
        };

        const userDocRef = doc(db, collectionMap[userType], primaryKey);
        const role = roleMap[userType];

        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          const userData = userDoc.data();
          login(userData, role);

          const pageMap = {
            _ADMIN_: '/admin',
            _STUDENT_: '/student',
            _TEACHER_: '/teacher',
          };
          navigate(pageMap[userType] || '/home');
        } else {
          console.error('No such document found!');
          clearLocalStorage();
          navigate('/home');
        }
      } catch (error) {
        console.error('Error fetching user info:', error);
        clearLocalStorage();
        navigate('/home');
      }
    };

    if (!isLoggedIn || !rememberMe || !userType || !primaryKey) {
      clearLocalStorage();
      navigate('/home');
    } else {
      fetchUserInfo();
    }
  }, []);

  const pageVariants = {
    initial: {
      opacity: 0,
      y: 20
    },
    animate: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    },
    exit: {
      opacity: 0,
      y: -20,
      transition: {
        duration: 0.3
      }
    }
  };

  return (
      <div className="App">
        <AnimatePresence mode="wait">
          <Routes location={location} key={location.pathname}>
            <Route path="/" element={<Navigate to="/home" />} />
            <Route path="/register" element={
              <motion.div
                initial="initial"
                animate="animate"
                exit="exit"
                variants={pageVariants}
              >
                <AdminRegister />
              </motion.div>
            } />
            <Route path="/student-signup" element={
              <motion.div
                initial="initial"
                animate="animate"
                exit="exit"
                variants={pageVariants}
              >
                <StudentSignUp />
              </motion.div>
            } />
            <Route path="/home" element={
              <motion.div
                initial="initial"
                animate="animate"
                exit="exit"
                variants={pageVariants}
              >
                <LoginPage />
              </motion.div>
            } />
            <Route path="/login" element={
              <motion.div
                initial="initial"
                animate="animate"
                exit="exit"
                variants={pageVariants}
              >
                <LoginPage />
              </motion.div>
            } />
            <Route
              path="/admin"
              element={
                <PrivateRoute>
                  <motion.div
                    initial="initial"
                    animate="animate"
                    exit="exit"
                    variants={pageVariants}
                  >
                    <AdminPage />
                  </motion.div>
                </PrivateRoute>
              }
            />
            <Route
              path="/student"
              element={
                <PrivateRoute>
                  <motion.div
                    initial="initial"
                    animate="animate"
                    exit="exit"
                    variants={pageVariants}
                  >
                    <StudentPage />
                  </motion.div>
                </PrivateRoute>
              }
            />
            <Route
              path="/teacher"
              element={
                <PrivateRoute>
                  <motion.div
                    initial="initial"
                    animate="animate"
                    exit="exit"
                    variants={pageVariants}
                  >
                    <TeacherPage />
                  </motion.div>
                </PrivateRoute>
              }
            />
            <Route path="*" element={<Navigate to="/home" />} />
            <Route path="/about" element={
              <motion.div
                initial="initial"
                animate="animate"
                exit="exit"
                variants={pageVariants}
              >
                <AboutUs />
              </motion.div>
            } />
            <Route path="/id-card" element={
              <motion.div
                initial="initial"
                animate="animate"
                exit="exit"
                variants={pageVariants}
              >
                <IdCard />
              </motion.div>
            } />
            <Route path="/id-card-ad" element={
              <motion.div
                initial="initial"
                animate="animate"
                exit="exit"
                variants={pageVariants}
              >
                <IdCardAd />
              </motion.div>
            } />
            <Route path="/get-id-card-prompt" element={
              <motion.div
                initial="initial"
                animate="animate"
                exit="exit"
                variants={pageVariants}
              >
                <GetIdCardPrompt />
              </motion.div>
            } />
            <Route 
              path="/student-id-card/:studentRegNumber" 
              element={
                <PrivateRoute>
                  <motion.div
                    initial="initial"
                    animate="animate"
                    exit="exit"
                    variants={pageVariants}
                  >
                    <StudentIdCard />
                  </motion.div>
                </PrivateRoute>
              } 
            />
          </Routes>
        </AnimatePresence>
      </div>
  );
}

export default App;
